// The Order page of the Codex: set each glyph you have seen in its one true
// place (src/domain/order.ts has the rules). A glyph set right locks with a
// bell; set wrong, it comes back and the rings that do not belong flash.

import { html, unsafeRawHtml, type Html } from "../boundary/html.ts"
import { WORDS } from "../config/words.ts"
import { isPlaced, litOrderLines, orderWhole, place, seenOfWidth, slotColumn, slotRow, stageWhole, stagesMet, type Stage } from "../domain/order.ts"
import type { Progress } from "../domain/progress.ts"
import { glyphKeyOf, ringsOf, valueOf, type GlyphKey, type Trit } from "../domain/rings.ts"
import type { Clock } from "../effects/clock.ts"
import type { Sound } from "../effects/sound.ts"
import { closest, parts, replay } from "./dom.ts"
import { realmOf, sigil } from "./glyph.ts"

export type OrderDeps = {
  readonly sound: Sound
  readonly clock: Clock
  readonly progress: () => Progress
  readonly change: (next: Progress) => void
  readonly rerender: () => void
}

export type OrderView = {
  readonly html: () => Html
  readonly drawn: () => void // called after the page draws, so each flourish plays once
}

type Fresh = { readonly slot: GlyphKey; readonly lines: readonly string[]; readonly stage: number }
type Drag = { readonly key: GlyphKey; readonly el: HTMLElement; readonly x0: number; readonly y0: number; readonly pointer: number; readonly float: HTMLElement | undefined }

const LAYERS: readonly (Trit | "all")[] = [-1, 0, 1]

const keyOfData = (el: HTMLElement | undefined, name: string): GlyphKey | undefined => glyphKeyOf(el?.dataset[name] ?? "")

export const createOrder = (panel: HTMLElement, deps: OrderDeps): OrderView => {
  const { sound } = deps
  let picked: GlyphKey | undefined
  let drag: Drag | undefined
  let fresh: Fresh | undefined
  let quietUntil = 0

  const slotHtml = (progress: Progress) => (p: GlyphKey): Html => {
    const t = ringsOf(p)
    const on = isPlaced(progress, p)
    const just = fresh?.slot === p ? " just" : ""
    return html`<button type="button" class="o-slot${on ? " locked" : ""}${just}" data-slot="${p}" data-realm="${realmOf(t)}" style="--x:${slotColumn(t)};--y:${slotRow(t)}" aria-label="${on ? WORDS.aria.placed : WORDS.aria.emptyPlace}">${on ? sigil(t) : ""}</button>`
  }

  const tabletHtml = (progress: Progress, s: Stage) => (z: Trit | "all"): Html => {
    const here = s.points.filter((p) => z === "all" || ringsOf(p)[2] === z)
    const lit = litOrderLines(progress, s)
    const freshLine = (pts: readonly GlyphKey[]): string => (fresh?.lines.includes(pts.join()) === true ? ' class="fresh"' : "")
    const inTablet = (pts: readonly GlyphKey[]): boolean => z === "all" || ringsOf(pts[0] ?? "0")[2] === z
    const segs = lit
      .filter((l) => l.axis < 2 && inTablet(l.pts))
      .map((l) => {
        const a = ringsOf(l.pts[0])
        const b = ringsOf(l.pts[2])
        return unsafeRawHtml(`<line${freshLine(l.pts)} x1="${slotColumn(a)}" y1="${slotRow(a)}" x2="${slotColumn(b)}" y2="${slotRow(b)}"/>`)
      })
    const pillars = lit
      .filter((l) => l.axis === 2)
      .map((l) => {
        const a = ringsOf(l.pts[0])
        return unsafeRawHtml(`<circle${freshLine(l.pts)} cx="${slotColumn(a)}" cy="${slotRow(a)}" r="0.36"/>`)
      })
    const rows = s.w === 1 ? 1 : 3
    const grid = [
      ...Array.from({ length: rows }, (_, r) => `<line x1="0" y1="${r}" x2="2" y2="${r}"/>`),
      ...(rows > 1 ? [0, 1, 2].map((c) => `<line x1="${c}" y1="0" x2="${c}" y2="2"/>`) : []),
    ].join("")
    const mark = z === "all" ? html`` : html`<div class="o-mark" data-realm="${realmOf([z])}">${sigil([z])}</div>`
    return html`<div class="o-tablet-wrap">${mark}<div class="o-tablet" style="--rows:${rows}">
          <svg class="tablet-lines" viewBox="-0.5 -0.5 3 ${rows}" preserveAspectRatio="none" aria-hidden="true">
            <g class="grid">${unsafeRawHtml(grid)}</g><g class="lit">${segs}</g><g class="pillars">${pillars}</g>
          </svg>${here.map(slotHtml(progress))}</div></div>`
  }

  const stageHtml = (progress: Progress) => (s: Stage): Html => {
    const layers = s.w === 3 ? LAYERS : (["all"] as const)
    const loose = seenOfWidth(progress, s.w).filter((k) => !isPlaced(progress, k))
    const palette = loose.map((k) => {
      const t = ringsOf(k)
      return html`<button type="button" class="o-glyph${picked === k ? " on" : ""}" data-key="${k}" data-realm="${realmOf(t)}" aria-label="${WORDS.aria.glyph}">${sigil(t)}</button>`
    })
    const freshStage = fresh?.stage === s.w ? " fresh" : ""
    return html`<section class="order-stage${stageWhole(progress, s) ? " whole" : ""}${freshStage}" data-w="${s.w}">
        <div class="order-tablets">${layers.map(tabletHtml(progress, s))}</div>
        <div class="o-palette">${palette}</div>
      </section>`
  }

  const pageHtml = (): Html => {
    const progress = deps.progress()
    const stages = stagesMet(progress)
    if (stages.length === 0) return html`<p class="panel-note">${WORDS.codex.orderEmpty}</p>`
    // the finale: every place of every stage filled
    const whole = stages.length === 3 && orderWhole(progress)
    const crown = whole ? html`<div class="order-whole${fresh !== undefined && fresh.stage > 0 ? " fresh" : ""}">${sigil(["folded", "folded", "folded"])}</div>` : html``
    return html`${crown}<p class="panel-note">${whole ? WORDS.codex.orderWhole : WORDS.codex.orderNote}</p>
      ${stages.map(stageHtml(progress))}`
  }

  // ---------- the law of the page ----------

  const refuse = (key: GlyphKey, slot: GlyphKey, faults: readonly number[]): void => {
    sound.refuse()
    const glyph = panel.querySelector(`.o-glyph[data-key="${key}"]`)
    const at = panel.querySelector(`.o-slot[data-slot="${slot}"]`)
    for (const el of [glyph, at]) if (el !== null) replay(el, "nope")
    if (glyph === null) return
    for (const r of faults) for (const g of parts(glyph, `[data-ring="${r}"]`)) replay(g, "fault")
    deps.clock.after(1100, () => {
      for (const g of parts(glyph, ".fault")) g.classList.remove("fault")
    })
  }

  const put = (key: GlyphKey, slot: GlyphKey): void => {
    const progress = deps.progress()
    if (isPlaced(progress, slot)) return
    const r = place(progress, key, slot)
    if (r.tag === "error") return refuse(key, slot, r.error.faults)
    picked = undefined
    const t = ringsOf(key)
    fresh = { slot: key, lines: r.value.lines.map((l) => l.pts.join()), stage: r.value.stageWhole ? t.length : 0 }
    deps.change(r.value.progress)
    sound.rest(valueOf(t), 1, t.length)
    r.value.lines.forEach((_, i) => deps.clock.after(420 + i * 260, () => sound.chime(6 + i * 3)))
    if (r.value.stageWhole) deps.clock.after(900, () => sound.monad(t.length))
    deps.rerender()
  }

  // ---------- hands ----------

  const slotUnder = (e: PointerEvent): HTMLElement | undefined => {
    const el = document.elementFromPoint(e.clientX, e.clientY)
    const slot = el?.closest(".o-slot")
    return slot instanceof HTMLElement && panel.contains(slot) ? slot : undefined
  }

  const endDrag = (): void => {
    const d = drag
    drag = undefined
    if (d === undefined) return
    d.float?.remove()
    d.el.classList.remove("away")
    for (const s of parts(panel, ".o-slot.hover")) s.classList.remove("hover")
  }

  panel.addEventListener("pointerdown", (e) => {
    const g = closest(e, ".o-glyph")
    const key = keyOfData(g, "key")
    if (g === undefined || key === undefined || e.button > 0) return
    e.preventDefault()
    sound.wake()
    drag = { key, el: g, x0: e.clientX, y0: e.clientY, pointer: e.pointerId, float: undefined }
  })

  window.addEventListener("pointermove", (e) => {
    const d = drag
    if (d === undefined || e.pointerId !== d.pointer) return
    let float = d.float
    if (float === undefined) {
      if (Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < 6) return
      sound.lift()
      const copy = d.el.cloneNode(true)
      if (!(copy instanceof HTMLElement)) return
      copy.classList.add("o-float")
      copy.classList.remove("on")
      document.body.append(copy)
      d.el.classList.add("away")
      float = copy
      drag = { ...d, float }
    }
    float.style.transform = `translate(${e.clientX - 26}px, ${e.clientY - 26}px)`
    for (const s of parts(panel, ".o-slot.hover")) s.classList.remove("hover")
    slotUnder(e)?.classList.add("hover")
  })

  window.addEventListener("pointerup", (e) => {
    const d = drag
    if (d === undefined || e.pointerId !== d.pointer) return
    if (d.float === undefined) {
      drag = undefined
      return // a tap: the click that follows picks the glyph
    }
    const slot = keyOfData(slotUnder(e), "slot")
    endDrag()
    quietUntil = deps.clock.now() + 300
    if (slot !== undefined) put(d.key, slot)
  })

  window.addEventListener("pointercancel", endDrag)

  panel.addEventListener("click", (e) => {
    if (deps.clock.now() < quietUntil) return
    const g = closest(e, ".o-glyph")
    const key = keyOfData(g, "key")
    if (key !== undefined) {
      sound.wake()
      picked = picked === key ? undefined : key
      if (picked !== undefined) sound.lift()
      for (const b of parts(panel, ".o-glyph")) b.classList.toggle("on", b.dataset["key"] === picked)
      return
    }
    const slot = keyOfData(closest(e, ".o-slot"), "slot")
    if (slot !== undefined && picked !== undefined) put(picked, slot)
  })

  return {
    html: pageHtml,
    drawn: () => {
      fresh = undefined
    },
  }
}
